import { some } from '@utils/some';
import {
  DiningOption,
  DynamicPackagesFormStepNames,
  FormStep,
  PackageRestaurant,
  PackageType,
} from '../types';
import { getPackageRestaurantChoices } from './diningRestaurantChoices';
import {
  getRepeatableExperienceOccurrenceDays,
  groupRepeatableExperiencesByDay,
} from './repeatableExperiencesUtils';

function toDiningOption(
  restaurant: PackageRestaurant,
  dayNumber: number,
  dayNumberRange: number[]
): DiningOption {
  const [firstChoice, ...additionalChoices] = getPackageRestaurantChoices(restaurant);
  return {
    ...firstChoice,
    ...(some(additionalChoices) && { restaurantChoices: [firstChoice, ...additionalChoices] }),
    name: restaurant.name ?? '',
    delayAfter: restaurant.delayAfter,
    dayNumber,
    dayNumberRange,
  };
}

export function getTablesSteps(
  restaurants: PackageRestaurant[],
  packageType: PackageType,
  stayDuration: number
): FormStep[] {
  let diningOptions: DiningOption[] = [];
  if (packageType !== PackageType.REPEATING) {
    restaurants.forEach((r) => {
      diningOptions.push(toDiningOption(r, r.dayNumber, r.dayNumberRange || [r.dayNumber]));
    });
  }
  if (packageType === PackageType.REPEATING) {
    diningOptions = distributeRepeatingPackageDining(restaurants, stayDuration);
  }
  return some(diningOptions)
    ? [
        {
          step: DynamicPackagesFormStepNames.TABLES,
          type: 'tables',
          diningOptions,
        },
      ]
    : [];
}

function distributeRepeatingPackageDining(
  restaurants: PackageRestaurant[],
  stayDuration: number
): DiningOption[] {
  const { day0, day1 } = groupRepeatableExperiencesByDay(restaurants);

  const diningOptions: DiningOption[] = [];

  for (let dayIndex = 0; dayIndex <= stayDuration; dayIndex++) {
    // Day 0 dining: Available on check-in through night before checkout
    const day0Days = getRepeatableExperienceOccurrenceDays({
      experienceDayNumber: 0,
      stayDuration,
    });
    if (day0Days.includes(dayIndex) && day0.length > 0) {
      day0.forEach((r) => {
        const baseDayNumberRange = r.dayNumberRange || [0];
        diningOptions.push(
          toDiningOption(
            r,
            dayIndex,
            baseDayNumberRange.map((day) => day + dayIndex)
          )
        );
      });
    }

    // Day 1 dining: Available day after check-in through checkout
    const day1Days = getRepeatableExperienceOccurrenceDays({
      experienceDayNumber: 1,
      stayDuration,
    });
    if (day1Days.includes(dayIndex) && day1.length > 0) {
      day1.forEach((r) => {
        const baseDayNumberRange = r.dayNumberRange || [1];
        diningOptions.push(
          toDiningOption(
            r,
            dayIndex,
            baseDayNumberRange.map((day) => day + dayIndex)
          )
        );
      });
    }
  }

  return diningOptions;
}
